import React, { useEffect, useState, useRef, useCallback } from "react";
import { securePost, secureGet, PostExcel } from "../../config/api";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { PrimaryButton } from "../../components/PrimaryButton";
import Filter from "../../components/Filter";
import Table from "../../components/Table";
import LoadingLogo from "../../components/LoadingLogo";
import PageScroller from "../../components/PageScroller";
import exportIcon from "../../icons/export.svg";
import { saveAs } from 'file-saver';
import SnackbarNotification from '../../components/SnackbarNotification';

const ProductsByRepo = () => {
  const [displayedData, setDisplayedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    stock: "0",
    marque: null,
    famille: null,
    enStock: null,
    sortBy: "",
    sortDirection: "asc",
    needle: "",
    onSale: null,
  });
  const [stocks, setStocks] = useState([]);
  const [brands, setBrands] = useState([]);
  const [families, setFamilies] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const { t } = useTranslation();
  const abortController = useRef(null);
  const abortControllerOptions = useRef(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const columns = [
    {
      label: (
        <div
          className="flex items-center cursor-pointer"
          onClick={() => handleSortChange("product.reference")}
        >
          {t(`columns.reference`)}
          {filters.sortBy === "product.reference" && (
            <span className="ml-1">{filters.sortDirection === "asc" ? "▲" : "▼"}</span>
          )}
        </div>
      ),
      key: "reference",
      render: (_, row) => row.product?.reference || "N/A",
    },
    {
      label: (
        <div
          className="flex items-center cursor-pointer"
          onClick={() => handleSortChange("product.name")}
        >
          {t(`columns.name`)}
          {filters.sortBy === "product.name" && (
            <span className="ml-1">{filters.sortDirection === "asc" ? "▲" : "▼"}</span>
          )}
        </div>
      ),
      key: "name",
      render: (_, row) => row.product?.name || "N/A",
    },
    {
      label: (
        <div
          className="flex items-center cursor-pointer"
          onClick={() => handleSortChange("product.brand.name")}
        >
          {t(`columns.brand`)}
          {filters.sortBy === "product.brand.name" && (
            <span className="ml-1">{filters.sortDirection === "asc" ? "▲" : "▼"}</span>
          )}
        </div>
      ),
      key: "brandName",
      render: (_, row) => row.product?.brand?.name || "N/A",
    },
    {
      label: (
        <div
          className="flex items-center cursor-pointer"
          onClick={() => handleSortChange("product.family.name")}
        >
          {t(`columns.family`)}
          {filters.sortBy === "product.family.name" && (
            <span className="ml-1">{filters.sortDirection === "asc" ? "▲" : "▼"}</span>
          )}
        </div>
      ),
      key: "familyName",
      render: (_, row) =>
        row.product?.family ? (
          <div
            style={{
              backgroundColor: row.product.family.color || "#ccc",
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "12px",
              textAlign: "center",
              display: "inline-block",
              fontSize: "14px",
            }}
          >
            {row.product.family.name}
          </div>
        ) : (
          "N/A"
        ),
    },
    {
      label: t(`columns.quantity`),
      key: "quantity",
      render: (_, row) => {
        const { quantity = 0, available = 0 } = row;
        const color = quantity === 0 && available === 0 ? "#EF476F" : quantity >= 30 ? "#0AD69F" : "#FEA26F";
        return <span style={{ color }}>{quantity || "00"}</span>;
      },
    },
  ];

  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    {
      name: "stock",
      label: "Stock",
      type: "dropdown",
      options: [{ value: "0", label: t(`placeholder.all`) }, ...stocks.map((stock) => ({ value: stock.id, label: stock.name }))],
    },
    {
      name: "marque",
      label: t(`filters.repo.brand`),
      type: "dropdown",
      options: [...brands.map((brand) => ({ value: brand.id, label: brand.name }))],
    },
    {
      name: "famille",
      label: t(`filters.repo.family`),
      type: "dropdown",
      options: [...families.map((family) => ({ value: family.id, label: family.name }))],
    },
    {
      name: "enStock",
      label: t(`filters.repo.inStock`),
      type: "dropdown",
      options: [
        { value: "ALL", label: t(`placeholder.all`) },
        { value: "AVAILABLE_ONLY", label: t(`options.available`) },
      ],
    },
    {
      name: "onSale",
      label: t(`filters.repo.onSale`),
      type: "dropdown",
      options: [
        { value: "ALL", label: t(`placeholder.all`) },
        { value: "true", label: t(`options.onSales`) },
      ],
    },
    {
      name: "sortBy",
      label: t(`filters.repo.sortBy`),
      type: "dropdown",
      options: [
        { label: t(`options.reference`), value: "product.reference" },
        { label: t(`options.name`), value: "product.name" },
        { label: t(`options.brandName`), value: "product.brand.name" },
        { label: t(`options.familyName`), value: "product.family.name" },
        { label: t(`options.availability`), value: "available" },
      ],
    },
  ];

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const signal = abortControllerOptions.current?.signal;

        const stockResponse = await secureGet("/repos", showSnackbar, { signal });
        setStocks(stockResponse.data.map((stock) => ({ id: stock.id, name: stock.name })));

        const brandResponse = await secureGet("/brands", showSnackbar, { signal });
        setBrands(brandResponse.data.map((brand) => ({ id: brand.id, name: brand.name })));

        const familyResponse = await secureGet("/families", showSnackbar, { signal });
        setFamilies(familyResponse.data.map((family) => ({ id: family.id, name: family.name })));
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Error fetching dropdown options:", error);
        }
      }
    };
    abortControllerOptions.current = new AbortController();
    fetchDropdownOptions();
    return () => {
      abortControllerOptions.current.abort();
    };
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    abortController.current?.abort();
    abortController.current = new AbortController();
    try {
      const requestBody = {
        availableOnly: filters.enStock === "AVAILABLE_ONLY",
        brandId: parseInt(filters.marque, 10),
        familyId: parseInt(filters.famille, 10),
        repoId: Number(filters.stock) || 0,
        needle: filters.needle || null,
        onSale: filters.onSale === "ALL" ? null : filters.onSale === "true",
      };
      const sortParam = filters.sortBy ? `&sort=${filters.sortBy},${filters.sortDirection || 'asc'}` : '';
      const response = await securePost(
        `/products/byRepo?page=${currentPage}&size=${pageSize}${sortParam}`,
        requestBody,
        showSnackbar,
        { signal: abortController.current.signal }
      );
      console.log("response", response);
      setDisplayedData(response.data.content || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error fetching products:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, pageSize]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchProducts();
    return () => {
      abortController.abort();
    };
  }, [filters, currentPage, pageSize, fetchProducts]);

  const handleFilterChange = (filterName, filterValue) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: filterValue,
    }));
  };

  const resetFilters = () => {
    setFilters({
      stock: "0",
      marque: null,
      famille: null,
      enStock: null,
      sortBy: "",
      sortDirection: "asc",
      needle: "",
      onSale: null,
    });
    setCurrentPage(0);
  };

  const handleSortChange = (key) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      sortBy: key,
      sortDirection: prevFilters.sortDirection === "asc" ? "desc" : "asc",
    }));
  };

  const handleExport = async () => {
    const requestBody = {
      availableOnly: filters.enStock === "AVAILABLE_ONLY",
      brandId: parseInt(filters.marque, 10),
      familyId: parseInt(filters.famille, 10),
      repoId: Number(filters.stock) || 0,
      needle: filters.needle || null,
      onSale: filters.onSale === "ALL" ? null : filters.onSale === "true",
    };
    console.log('Exporting with filters:', requestBody);
    try {
      const response = await PostExcel('/products/exportByRepo', requestBody, showSnackbar);
      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0]; 
      const fileName = `stock_${formattedDate}.xlsx`;
      const blob = new Blob([response.data], {
        type: 'application/vnd.ms-excel',
      });
      saveAs(blob, fileName);
    } catch (error) {
      console.error("Export failed:", error);
    }
    setLoading(false);
  }

  return (
    <div>
      <Helmet>
        <title>Drivnest - {t("titles.repo")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t(`titles.repo`)}</h1>
        <div className="ml-auto text-[16px]">
          <PrimaryButton
            label={t(`actions.export`)}
            icon={exportIcon}
            onClick={() => handleExport()}
          />
        </div>
      </div>

      <Filter filters={filterOptions} onFilterChange={handleFilterChange} resetFilters={resetFilters} filterValues={filters} />

      <div className="table-container" style={{ position: "relative", minHeight: "200px" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            <LoadingLogo />
          </div>
        )}
        <Table columns={columns} data={displayedData} actions={[]} />
      </div>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default ProductsByRepo;

